import { AmountSuperScript } from "components/design/AmountSuperScript";
import Loader from "components/DesignSystem/Loader/loader";
import { TableUI } from "components/design/TableUI";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  AggregateEntryTxns,
  useGetAggregateEntryTxnsQuery,
} from "store/apis/generalLedger";
import { openSlider } from "store/slices/transactions";

type EntryLinkedTxnTableProps = {
  txnId: string;
  entityId: string;
};

const columnHelper = createColumnHelper<AggregateEntryTxns>();

const columns = [
  columnHelper.accessor("date", {
    size: 27,
    cell: (info) => (
      <div className="t-text-body-sm t-text-text-60 t-py-2">
        {dayjs(info.getValue()).format(DISPLAY_DATE)}
      </div>
    ),
  }),
  columnHelper.accessor("merchant_data", {
    size: 25,
    cell: (info) => {
      const { logo, name } = info.getValue() || {};
      if (!Boolean(name)) {
        return "-";
      }
      return (
        <div className="t-flex t-gap-2 t-items-center">
          <Avatar src={logo || ""} alt={name} />
          <div className="t-text-body-sm t-text-text-60">{name}</div>
        </div>
      );
    },
  }),
  columnHelper.accessor("amount", {
    size: 15,
    id: "debit",
    cell: (info) => {
      if (info.getValue() < 0) {
        return (
          <span className="t-text-body-sm t-text-text-60 t-flex t-items-end t-justify-end">
            <AmountSuperScript amount={Number(info.getValue())} />
          </span>
        );
      }
    },
  }),
  columnHelper.accessor("amount", {
    size: 15,
    id: "credit",
    cell: (info) => {
      if (info.getValue() >= 0) {
        return (
          <span className="t-text-body-sm t-text-text-60 t-flex t-items-end t-justify-end">
            <AmountSuperScript amount={Number(info.getValue())} />
          </span>
        );
      }
    },
  }),
  columnHelper.accessor("amount", {
    size: 17,
    id: "credit",
    cell: (info) => {
      return null;
    },
  }),
];

export default function EntryLinkedTxnTable({
  txnId,
  entityId,
}: EntryLinkedTxnTableProps) {
  const dispatch = useDispatch();
  const {
    data: transactions = [],
    isFetching,
    isSuccess,
  } = useGetAggregateEntryTxnsQuery(
    {
      entityId,
      transactionId: txnId,
    },
    {
      skip: !txnId || !entityId,
    }
  );

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 10,
    },
  });

  const handleRowClick = (transactionId: string) => {
    dispatch(openSlider(transactionId));
  };

  return (
    <tr className="t-w-full">
      <Async.Root
        isLoading={isFetching}
        isSuccess={isSuccess}
        isEmpty={transactions.length === 0}
        customLoader={
          <td className="t-w-full !t-py-6 t-pl-4" colSpan={6}>
            <div className="t-w-full t-flex t-justify-center">
              <Loader size="small" />
            </div>
          </td>
        }
      >
        <Async.Empty>
          <td className="t-w-full !t-py-6 t-pl-4" colSpan={6}>
            <div className="t-text-text-60 t-text-body-sm t-text-center t-w-full t-p-1.5 t-flex t-justify-center">
              No transactions found
            </div>
          </td>
        </Async.Empty>
        <Async.Success>
          <td className="t-w-full !t-py-6 t-pl-4" colSpan={6}>
            <TableUI
              table={table}
              onRowClick={(row) => handleRowClick(row.original.uuid)}
              hideHeader
            />
          </td>
        </Async.Success>
      </Async.Root>
    </tr>
  );
}
